// API-Client: fetch-Wrapper um das Fastify-Backend
// Packt JWT-Token automatisch, entpackt ACK-Envelope, wirft bei Fehlern

const BASE = '/api'

// ACK-Envelope Format des Backends
export interface AckResponse<T = unknown> {
  ack: 0 | 1
  data?: T
  error?: Array<{ code: string; message: string; field?: string }>
  warning?: Array<{ code: string; message: string }>
  pagination?: {
    next_min_id?: string | number
    has_more: boolean
    total?: number
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    // RA4-WEB-2: true wenn diese ApiError NICHT aus einer erfolgreich dekodierten
    // ACK-Envelope stammt, sondern weil res.json() eine SyntaxError geworfen hat
    // (z.B. ein Proxy-502/503 mit HTML-Fehlerseite statt JSON). In dem Fall kennen
    // wir den fachlichen Ausgang NICHT — der Status allein reicht nicht, um "klarer
    // 4xx" zu behaupten, weil wir den ACK-error-Body nie gesehen haben.
    public readonly undecodable: boolean = false,
    // Produkt-Wissen (Task 12): manche Endpunkte haengen fachliche Zusatzfelder direkt
    // an die ack:0-Envelope (nicht in error[0]) — z.B. POST /product-knowledge/:sku/enrich/
    // bei LOCKED: { error:[{code:'LOCKED',...}], lockedBy, lockedAt }. error[0] allein
    // reicht dem Aufrufer dann nicht (der Dialog braucht lockedBy/lockedAt). `details`
    // traegt den Rest der Envelope (ohne ack/warning/error/pagination/data) unveraendert
    // durch, ohne dass jeder Aufrufer den Envelope-Rohbau selbst nachbauen muss.
    public readonly details: Record<string, unknown> = {},
    // Plan 2026-10-20 Teil A: Feld aus error[0].field — z. B. 'paare_bestaetigt' heisst
    // „Rueckfrage beantworten und mit demselben Key erneut senden“.
    public readonly field: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** RA3-WEB-B: gemeinsame Klassifizierung "Transport-Fehler" (Netz/401/5xx — Ausgang
 *  unklar, der Server hat den Request evtl. bereits verarbeitet, nur die Antwort ging
 *  verloren) vs. ein klarer fachlicher 4xx-Fehler (Server hat definitiv geantwortet:
 *  Validierung/Ablehnung — NICHT ambig, nichts wurde committet). Netzfehler landen als
 *  `TypeError` (fetch wirft das bei DNS-/Verbindungsfehlern), 401 wird hier bewusst wie
 *  ein Transportfehler behandelt (Session-Klemmer, kein fachlicher Business-Fehler).
 *  Bereits vorher inline in `sendMove` (api/warehouse.ts) verwendet — hier als geteilter,
 *  testbarer Helfer extrahiert, damit andere Aufrufer (z.B. UmfahrenPage assign/fremd)
 *  dieselbe Klassifizierung nutzen.
 *
 *  RA4-WEB-2 (Re-Audit Runde 3, Welle W4-WEB): ein `res.json()`-Decode-Fehler (Body ist
 *  kein JSON — z.B. eine Proxy-502/503-HTML-Seite) wurde vorher GAR NICHT als ApiError
 *  geworfen, sondern als roher `SyntaxError` — den kannte `isTransportError` nicht, die
 *  Aufrufer stuften ihn faelschlich als "klarer fachlicher Fehler" ein und loesten z.B.
 *  den assign/cancel-Fehler-Lock faelschlich wieder. Entscheidung (dokumentiert statt nur
 *  angenommen): `apiFetch`/`apiFetchFull` faengen den Decode-Fehler jetzt selbst und werfen
 *  eine `ApiError` mit `undecodable:true` + dem echten HTTP-Status. Ein UNDEKODIERBARER
 *  Body ist bei JEDEM Status ambig — wir haben den ACK-error-Body nie gesehen und koennen
 *  daher nicht behaupten, es sei ein "klarer 4xx" (ein Proxy/Gateway kann technisch jeden
 *  Status mit einer nicht-JSON-Fehlerseite ausliefern). Deshalb: `undecodable === true` ⇒
 *  IMMER Transport/ambig, unabhaengig vom Status. Ein DEKODIERBARER 4xx mit echtem
 *  ACK-error-Body bleibt weiterhin klar (kein Transport-Fehler) — nur >=500/401 zaehlen
 *  dort als ambig, wie bisher. */
export function isTransportError(err: unknown): boolean {
  return (
    err instanceof TypeError ||
    (err instanceof ApiError && (err.status === 401 || err.status >= 500 || err.undecodable))
  )
}

function getToken(): string | null {
  return localStorage.getItem('stahlia_token')
}

export function setToken(token: string): void {
  localStorage.setItem('stahlia_token', token)
}

export function clearToken(): void {
  localStorage.removeItem('stahlia_token')
  localStorage.removeItem('stahlia_user')
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string> | undefined),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { ...options, headers })

  // 401 → Token ungültig → ausloggen
  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'UNAUTHORIZED', 'Sitzung abgelaufen')
  }

  let body: AckResponse<T> & Record<string, unknown>
  try {
    body = (await res.json()) as AckResponse<T> & Record<string, unknown>
  } catch {
    // RA4-WEB-2: Body ist kein valides JSON (z.B. Proxy-502/503-HTML) — Ausgang
    // unklar, siehe isTransportError()-Kommentar. undecodable:true macht das
    // sichtbar, unabhaengig vom HTTP-Status.
    throw new ApiError(res.status, 'DECODE_ERROR', `HTTP ${res.status} (Antwort nicht lesbar)`, true)
  }

  if (!res.ok || body.ack === 0) {
    const err = body.error?.[0]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ack: _ack, warning: _warning, error: _error, pagination: _pagination, data: _data, ...details } = body
    throw new ApiError(
      res.status,
      err?.code ?? 'API_ERROR',
      err?.message ?? `HTTP ${res.status}`,
      false,
      details,
      err?.field ?? null,
    )
  }

  // Backend nutzt zwei Patterns:
  //   List-Endpoints: { ack, data: [...], pagination }
  //   Detail-Endpoints: { ack, item: {...} } oder { ack, order: {...} }
  // Wenn `data` gesetzt ist, return das. Sonst: alles außer Envelope-Felder zurückgeben.
  if (body.data !== undefined) return body.data as T
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ack, warning, error, pagination, data, ...rest } = body
  return rest as T
}

/** Authentifizierter Binär-GET für geschützte Medienendpunkte ohne ACK-Envelope. */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { method: 'GET', headers })
  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'UNAUTHORIZED', 'Sitzung abgelaufen')
  }
  if (!res.ok) {
    let message = `Datei konnte nicht geladen werden (HTTP ${res.status})`
    try {
      const errBody = (await res.json()) as { error?: Array<{ message?: string }> }
      const first = errBody?.error?.[0]?.message
      if (first) message = first
    } catch {
      // Kein JSON-Body (z. B. rohe Proxy-HTML-Seite)
    }
    throw new ApiError(res.status, 'BLOB_FETCH_ERROR', message)
  }
  return res.blob()
}

/** Authentifizierter Multipart-Upload ohne automatischen Content-Type-Header. */
export async function apiUpload<T = unknown>(
  path: string,
  form: FormData,
): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { method: 'POST', body: form, headers })

  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'UNAUTHORIZED', 'Sitzung abgelaufen')
  }

  let body: AckResponse<T> & Record<string, unknown>
  try {
    body = (await res.json()) as AckResponse<T> & Record<string, unknown>
  } catch {
    throw new ApiError(res.status, 'DECODE_ERROR', `HTTP ${res.status} (Antwort nicht lesbar)`, true)
  }

  if (!res.ok || body.ack === 0) {
    const err = body.error?.[0]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ack: _ack, warning: _warning, error: _error, pagination: _pagination, data: _data, ...details } = body
    throw new ApiError(
      res.status,
      err?.code ?? 'API_ERROR',
      err?.message ?? `HTTP ${res.status}`,
      false,
      details,
      err?.field ?? null,
    )
  }

  if (body.data !== undefined) return body.data as T
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ack, warning, error, pagination, data, ...rest } = body
  return rest as T
}

// Hilfsfunktionen für gebräuchliche HTTP-Methoden
export const api = {
  get: <T>(path: string) => apiFetch<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body), ...(headers ? { headers } : {}) }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
}

// Fetch mit vollständiger AckResponse (für Pagination)
export async function apiFetchFull<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<AckResponse<T>> {
  const token = getToken()
  const headers: Record<string, string> = {
    ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string> | undefined),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, { ...options, headers })

  if (res.status === 401) {
    clearToken()
    window.location.href = '/login'
    throw new ApiError(401, 'UNAUTHORIZED', 'Sitzung abgelaufen')
  }

  let body: AckResponse<T>
  try {
    body = (await res.json()) as AckResponse<T>
  } catch {
    // RA4-WEB-2 (siehe apiFetch oben) — gleiche Behandlung fuer den Pagination-Pfad.
    throw new ApiError(res.status, 'DECODE_ERROR', `HTTP ${res.status} (Antwort nicht lesbar)`, true)
  }

  if (!res.ok || body.ack === 0) {
    const err = body.error?.[0]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ack: _ack, warning: _warning, error: _error, pagination: _pagination, data: _data, ...details } = body as unknown as Record<string, unknown>
    throw new ApiError(
      res.status,
      err?.code ?? 'API_ERROR',
      err?.message ?? `HTTP ${res.status}`,
      false,
      details,
      err?.field ?? null,
    )
  }

  return body
}
